import { useState, useRef, useEffect } from 'react';
import { X, MapPin, Phone, Mail, Calendar, Package, Plus, Trash2, Megaphone, Camera, Youtube, AlertCircle } from 'lucide-react';
import { apiUrl } from '../utils/api';

interface ProductItem {
  name: string;
  totalNeeded: number | string;
  unit: string;
}

interface PhotoFile {
  file: File;
  preview: string;
}

interface VideoLink {
  url: string;
  caption: string;
}

interface CreateCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

async function compressImage(file: File, maxSizeKB: number): Promise<File> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      const maxWidth = 1200;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, width, height);
      let quality = 0.8;
      const tryCompress = () => {
        canvas.toBlob(
          (blob) => {
            if (blob && (blob.size <= maxSizeKB * 1024 || quality <= 0.3)) {
              resolve(new File([blob], file.name, { type: 'image/jpeg' }));
            } else {
              quality -= 0.1;
              tryCompress();
            }
          },
          'image/jpeg',
          quality
        );
      };
      tryCompress();
    };
    img.src = URL.createObjectURL(file);
  });
}

const getInitialFormData = (user: any) => ({
  title: '',
  description: '',
  deadline: '',
  city: '',
  state: '',
  address: '',
  phone: user?.phoneNumber || '',
  email: user?.email || '',
});

const getInitialProducts = (): ProductItem[] => [
  { name: '', totalNeeded: 1, unit: '' }
];

export function CreateCampaignModal({ isOpen, onClose, onSuccess }: CreateCampaignModalProps) {
  const userData = localStorage.getItem('user');
  const user = userData ? JSON.parse(userData) : null;

  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [stepErrors, setStepErrors] = useState<string[]>([]);
  const [quantityTouched, setQuantityTouched] = useState<Record<number, boolean>>({});

  const [formData, setFormData] = useState(getInitialFormData(user));
  const [products, setProducts] = useState<ProductItem[]>(getInitialProducts());
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [videoLinks, setVideoLinks] = useState<VideoLink[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [compressing, setCompressing] = useState(false);

  // Colombia departments and cities data
  const [colombiaData, setColombiaData] = useState<{ id: number; departamento: string; ciudades: string[] }[]>([]);
  const [cities, setCities] = useState<string[]>([]);

  useEffect(() => {
    fetch('https://raw.githubusercontent.com/marcovega/colombia-json/master/colombia.json')
      .then(res => res.json())
      .then(data => setColombiaData(data))
      .catch(() => {});
  }, []);

  // Update cities when department changes
  useEffect(() => {
    if (formData.state && colombiaData.length > 0) {
      const dept = colombiaData.find(d => d.departamento === formData.state);
      setCities(dept ? dept.ciudades : []);
    } else {
      setCities([]);
    }
  }, [formData.state, colombiaData]);

  // Reset modal state every time it opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setIsLoading(false);
      setError('');
      setStepErrors([]);
      setQuantityTouched({});
      setFormData(getInitialFormData(user));
      setProducts(getInitialProducts());
      setPhotos([]);
      setVideoLinks([]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
    if (stepErrors.length) setStepErrors([]);
  };

  const handleProductChange = (index: number, field: keyof ProductItem, value: string | number) => {
    const updated = [...products];
    updated[index] = { ...updated[index], [field]: value };
    setProducts(updated);
    if (error) setError('');
    if (stepErrors.length) setStepErrors([]);
  };

  // Allow clearing quantity field freely, validate on blur
  const handleQuantityChange = (index: number, value: string) => {
    const updated = [...products];
    // Store raw string value - allow empty
    updated[index] = { ...updated[index], totalNeeded: value };
    setProducts(updated);
  };

  const handleQuantityBlur = (index: number) => {
    setQuantityTouched(prev => ({ ...prev, [index]: true }));
    const val = products[index].totalNeeded;
    // If they typed a valid number, normalize it
    if (val !== '' && Number(val) >= 1) {
      const updated = [...products];
      updated[index] = { ...updated[index], totalNeeded: parseInt(String(val), 10) };
      setProducts(updated);
    }
    // If empty or invalid, leave as-is — the red border will show
  };

  const isQuantityInvalid = (index: number) => {
    const val = products[index].totalNeeded;
    const touched = quantityTouched[index];
    if (!touched) return false;
    return val === '' || Number(val) < 1 || isNaN(Number(val));
  };

  const addProduct = () => {
    setProducts([...products, { name: '', totalNeeded: 1, unit: '' }]);
  };

  const removeProduct = (index: number) => {
    if (products.length > 1) {
      setProducts(products.filter((_, i) => i !== index));
    }
  };

  // Photo handling
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const remaining = 5 - photos.length;
    if (remaining <= 0) return;

    setCompressing(true);
    try {
      const newPhotos: PhotoFile[] = [];
      for (const file of files.slice(0, remaining)) {
        if (!['image/jpeg', 'image/png', 'image/webp', 'image/jpg'].includes(file.type)) continue;
        let processedFile = file;
        if (file.size > 400 * 1024) {
          processedFile = await compressImage(file, 400);
        }
        newPhotos.push({ file: processedFile, preview: URL.createObjectURL(processedFile) });
      }
      setPhotos([...photos, ...newPhotos]);
    } catch { /* ignore */ }
    finally {
      setCompressing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index: number) => {
    URL.revokeObjectURL(photos[index].preview);
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const addVideoLink = () => setVideoLinks([...videoLinks, { url: '', caption: '' }]);
  const removeVideoLink = (index: number) => setVideoLinks(videoLinks.filter((_, i) => i !== index));
  const updateVideoLink = (index: number, field: 'url' | 'caption', value: string) => {
    const updated = [...videoLinks];
    updated[index] = { ...updated[index], [field]: value };
    setVideoLinks(updated);
  };

  const today = new Date().toLocaleDateString('en-CA');

  // Validation per step - returns list of missing fields
  const getStepValidationErrors = (s: number): string[] => {
    const errors: string[] = [];
    if (s === 1) {
      if (!formData.title.trim()) errors.push('Título');
      if (!formData.description.trim()) errors.push('Descripción');
      if (!formData.deadline) errors.push('Fecha límite');
    }
    if (s === 2) {
      if (!formData.state.trim()) errors.push('Departamento');
      if (!formData.city.trim()) errors.push('Ciudad');
      if (!formData.address.trim()) errors.push('Dirección');
      if (formData.phone.trim().length < 10) errors.push('Teléfono (mínimo 10 dígitos)');
      if (!formData.email.includes('@')) errors.push('Email válido');
    }
    if (s === 3) {
      const hasAtLeastOneProduct = products.some(p => p.name.trim());
      if (!hasAtLeastOneProduct) {
        errors.push('Al menos un producto con nombre');
      } else {
        products.forEach((p, i) => {
          if (p.name.trim()) {
            if (!p.unit.trim()) errors.push(`Producto ${i + 1}: falta unidad de medida`);
            if (!p.totalNeeded || Number(p.totalNeeded) < 1) errors.push(`Producto ${i + 1}: falta cantidad`);
          }
        });
      }
    }
    return errors;
  };

  const canGoNext = () => {
    return getStepValidationErrors(step).length === 0;
  };

  const handleNext = () => {
    const errors = getStepValidationErrors(step);
    if (errors.length > 0) {
      setStepErrors(errors);
      return;
    }
    setStepErrors([]);
    setStep(step + 1);
  };

  const handleSubmit = async () => {
    // Validate all steps before submitting
    const allErrors: string[] = [];
    const step1Errors = getStepValidationErrors(1);
    const step2Errors = getStepValidationErrors(2);
    const step3Errors = getStepValidationErrors(3);

    if (step1Errors.length > 0) {
      allErrors.push(`Paso 1 (Info básica): ${step1Errors.join(', ')}`);
    }
    if (step2Errors.length > 0) {
      allErrors.push(`Paso 2 (Ubicación y contacto): ${step2Errors.join(', ')}`);
    }
    if (step3Errors.length > 0) {
      allErrors.push(`Paso 3 (Productos): ${step3Errors.join(', ')}`);
    }

    if (allErrors.length > 0) {
      setError(`Campos incompletos:\n${allErrors.join('\n')}`);
      return;
    }

    const validProducts = products.filter(p => p.name.trim() && p.unit.trim() && Number(p.totalNeeded) > 0);
    if (validProducts.length === 0) {
      setError('Paso 3 (Productos): Agrega al menos un producto completo (nombre, cantidad y unidad)');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(apiUrl('/api/campaigns'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: formData.title.trim(),
          description: formData.description.trim(),
          deadline: new Date(formData.deadline + 'T23:59:59').toISOString(),
          location: {
            country: 'Colombia',
            state: formData.state.trim() || formData.city.trim(),
            city: formData.city.trim(),
            address: formData.address.trim(),
          },
          contactInfo: {
            phone: formData.phone.trim(),
            email: formData.email.trim(),
          },
          privacyMode: 'private',
          campaignType: 'solidarity',
          coAdministrators: [],
        }),
      });

      const result = await response.json();

      if (result.success) {
        const campaignId = result.data.id;

        // Create products
        await Promise.all(
          validProducts.map(product =>
            fetch(apiUrl('/api/products'), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                campaignId,
                name: product.name.trim(),
                totalNeeded: Number(product.totalNeeded),
                unit: product.unit.trim(),
              }),
            })
          )
        );

        // Upload photos if any
        if (photos.length > 0) {
          try {
            const fd = new FormData();
            photos.forEach(p => fd.append('photos', p.file));
            await fetch(apiUrl(`/api/campaigns/${campaignId}/media/photos`), {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
              body: fd,
            });
          } catch { /* non-blocking */ }
        }

        // Add video links if any
        const validVideos = videoLinks.filter(v => v.url.trim());
        if (validVideos.length > 0) {
          try {
            await fetch(apiUrl(`/api/campaigns/${campaignId}/media/videos`), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ videos: validVideos }),
            });
          } catch { /* non-blocking */ }
        }

        onSuccess();
        onClose();
      } else {
        setError(result.error?.message || 'Error al crear la campaña');
      }
    } catch {
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
        {/* Overlay */}
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
            <div className="flex items-center">
              <Megaphone className="w-5 h-5 text-indigo-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Nueva Campaña</h2>
            </div>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Steps indicator */}
          <div className="px-6 pt-4">
            <div className="flex items-center justify-between mb-6">
              {[1, 2, 3, 4].map(s => (
                <div key={s} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= s ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {s}
                  </div>
                  {s < 4 && <div className={`w-12 sm:w-16 h-0.5 mx-1 ${step > s ? 'bg-indigo-600' : 'bg-gray-200'}`} />}
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="px-6 pb-6">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm whitespace-pre-line">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                {error}
              </div>
            )}

            {stepErrors.length > 0 && (
              <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-lg text-sm">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Campos obligatorios faltantes:
                <ul className="list-disc list-inside mt-1">
                  {stepErrors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}

            {/* Step 1: Info básica */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Título de la campaña <span className="text-red-500">*</span></label>
                  <input
                    name="title" value={formData.title} onChange={handleChange}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    placeholder="Ej: Ayuda para familia damnificada"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descripción <span className="text-red-500">*</span></label>
                  <textarea
                    name="description" value={formData.description} onChange={handleChange} rows={3}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    placeholder="¿Qué necesitas y por qué?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="w-4 h-4 inline mr-1" /> Fecha límite <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date" name="deadline" value={formData.deadline} onChange={handleChange} min={today}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                </div>
              </div>
            )}

            {/* Step 2: Ubicación y contacto */}
            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <MapPin className="w-4 h-4 inline mr-1" /> Departamento <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={(e) => {
                      setFormData({ ...formData, state: e.target.value, city: '' });
                      if (error) setError('');
                      if (stepErrors.length) setStepErrors([]);
                    }}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  >
                    <option value="">Selecciona un departamento</option>
                    {colombiaData.map(dept => (
                      <option key={dept.id} value={dept.departamento}>{dept.departamento}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ciudad <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="city"
                    value={formData.city}
                    onChange={(e) => {
                      setFormData({ ...formData, city: e.target.value });
                      if (error) setError('');
                      if (stepErrors.length) setStepErrors([]);
                    }}
                    disabled={!formData.state}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">{formData.state ? 'Selecciona una ciudad' : 'Primero selecciona un departamento'}</option>
                    {cities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dirección <span className="text-red-500">*</span></label>
                  <input
                    name="address" value={formData.address} onChange={handleChange}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    placeholder="Ej: Calle 10 #15-25, Barrio Centro"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Phone className="w-4 h-4 inline mr-1" /> Teléfono de contacto <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="phone" value={formData.phone} onChange={handleChange} type="tel"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    placeholder="+573001234567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Mail className="w-4 h-4 inline mr-1" /> Email de contacto <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="email" value={formData.email} onChange={handleChange} type="email"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    placeholder="contacto@email.com"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Productos */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    <Package className="w-4 h-4 inline mr-1" /> ¿Qué productos necesitas?
                  </p>
                </div>

                {products.map((product, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-indigo-600">Producto {index + 1}</span>
                      {products.length > 1 && (
                        <button type="button" onClick={() => removeProduct(index)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <input
                      value={product.name}
                      onChange={(e) => handleProductChange(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Nombre del producto *"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="number" min="1"
                        value={product.totalNeeded}
                        onChange={(e) => handleQuantityChange(index, e.target.value)}
                        onBlur={() => handleQuantityBlur(index)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Cantidad *"
                      />
                      <input
                        value={product.unit}
                        onChange={(e) => handleProductChange(index, 'unit', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Unidad (kg, unid...) *"
                      />
                    </div>
                  </div>
                ))}

                <button
                  type="button" onClick={addProduct}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" /> Agregar otro producto
                </button>
              </div>
            )}

            {/* Step 4: Fotos y Videos */}
            {step === 4 && (
              <div className="space-y-5">
                <p className="text-sm text-gray-600">
                  Agrega fotos y videos para mostrar a quién ayudarás. Esto genera confianza en los donantes.
                </p>

                {/* Photos */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Camera className="w-4 h-4 inline mr-1" /> Fotos ({photos.length}/5)
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {photos.map((photo, index) => (
                      <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 group">
                        <img src={photo.preview} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button" onClick={() => removePhoto(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white p-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {photos.length < 5 && (
                      <button
                        type="button" onClick={() => fileInputRef.current?.click()}
                        disabled={compressing}
                        className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-indigo-400 flex flex-col items-center justify-center text-gray-400 hover:text-indigo-500 transition-colors"
                      >
                        {compressing ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-500" />
                        ) : (
                          <>
                            <Plus className="w-5 h-5" />
                            <span className="text-xs mt-1">Foto</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  <input
                    ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple
                    className="hidden" onChange={handleFileSelect}
                  />
                  <p className="text-xs text-gray-500 mt-1.5">JPG, PNG o WebP. Máx 400KB (se comprimen automáticamente).</p>
                </div>

                {/* Videos */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Youtube className="w-4 h-4 inline mr-1" /> Enlaces de video (opcional)
                  </label>
                  <div className="space-y-2">
                    {videoLinks.map((video, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          value={video.url}
                          onChange={(e) => updateVideoLink(index, 'url', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="https://youtube.com/watch?v=..."
                        />
                        <button type="button" onClick={() => removeVideoLink(index)} className="text-red-500 hover:text-red-700 p-2">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button" onClick={addVideoLink}
                      className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Agregar enlace de video
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5">YouTube, TikTok, Instagram, Facebook o Vimeo.</p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-700">
                    💡 Este paso es opcional. Puedes agregar fotos y videos después desde la edición de tu campaña.
                  </p>
                </div>

                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                  <p className="text-xs text-indigo-800 font-medium mb-1">✅ Tu campaña empieza como privada</p>
                  <p className="text-xs text-indigo-700 mb-2">
                    Comparte el link con tu red de confianza. Si representas una organización y quieres aparecer en el marketplace público, puedes solicitar verificación.
                  </p>
                  <a
                    href={`https://wa.me/573169277199?text=${encodeURIComponent('Hola, quiero solicitar verificación para mi campaña en DonaApp. Mi correo registrado es: ')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-xs font-medium text-indigo-700 hover:text-indigo-900 transition-colors"
                  >
                    Solicitar verificación →
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-between rounded-b-xl">
            {step > 1 ? (
              <button onClick={() => { setStep(step - 1); setStepErrors([]); }} className="px-4 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
                Atrás
              </button>
            ) : (
              <button onClick={onClose} className="px-4 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancelar
              </button>
            )}

            {step < 4 ? (
              <button
                onClick={handleNext}
                className="px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {isLoading ? 'Creando...' : 'Crear Campaña'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
